import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { WelcomeView } from './WelcomeView';
import { useTrackFunnel, FUNNEL, logEvent, trackSignUp } from '../lib/telemetry';
import { getTraceContext } from '../lib/telemetry/appInsights';
import { errorType } from '../lib/telemetry/errorType';
import { api, bearer } from '../api/client';
import { organisationNameFromEmail } from '../lib/organisationNameFromEmail';
import { mapCreateOrgError, shouldEscalate, type CreateOrgErrorView } from '../lib/createOrganisationErrors';
import { ENTERPRISE_URL } from '../lib/scenarioNav';
import { Center, Loader } from '@mantine/core';
import { fetchOnboardingProfile, updateName, type OnboardingProfile } from '../api/onboarding';
import type { CreateOrganisationValues } from '../components/CreateOrganisationForm';
import { INVALID_NAME_MESSAGE } from '../lib/validateName';
import { markNameHandled } from '../lib/nameHandled';
import { tokens } from '../theme';
import { useFlagsSettled, useNameGateEnabled } from '../lib/featureFlags';

const RATE_LIMIT_RETRY_MS = 1_000;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// AuthKit's context isn't fully typed for the members we need; narrow to them here.
type AuthShape = {
  signOut: () => void;
  user?: { id?: string; email?: string | null; firstName?: string | null; lastName?: string | null } | null;
  getAccessToken?: () => Promise<string | null | undefined>;
  switchToOrganization?: (opts: { organizationId: string }) => Promise<void>;
};

// Container: prefills the name and owns the create-organisation
// submit (POST → session switch → route). Thin SDK/router glue — covered by E2E, not
// unit-mocked (see useStartTrial for the same convention).
export function Welcome() {
  const auth = useAuth() as AuthShape;
  const trackFunnel = useTrackFunnel();
  const navigate = useNavigate();
  // With `release-hub-onboarding-name-gate` off or not yet known this page is main's: no profile
  // call, no loader, no name fields, the organisation name from the email domain. A flag that
  // reads on later adds the name step to the form already on screen.
  const flagsSettled = useFlagsSettled();
  const nameGateOn = useNameGateEnabled() && flagsSettled;

  useEffect(() => { trackFunnel(FUNNEL.WELCOME_VIEWED); }, [trackFunnel]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<CreateOrgErrorView | null>(null);
  const [systemFailures, setSystemFailures] = useState(0);
  const [escalated, setEscalated] = useState(false);
  const [profile, setProfile] = useState<OnboardingProfile | null>(null);
  // Only a flag already on when the page opens holds the form for the profile (and its suggested
  // organisation name); a later one fills in the name step without taking the form away.
  const [holdForProfile, setHoldForProfile] = useState(nameGateOn);
  const [namesError, setNamesError] = useState<string | null>(null);
  // A retry after a failed session switch returns the same org; count the conversion once.
  const signUpSentFor = useRef<string | null>(null);
  // PUT /me/name is rate-limited (5/min/user) and `askName` stays true after a save, so a
  // retry after a slug collision or a 5xx must not send the same name again — only an edited one.
  const savedName = useRef<{ firstName: string; lastName: string } | null>(null);

  // Without a profile the session user is the best evidence: ask only when AuthKit has no name either.
  function profileFallback(): OnboardingProfile {
    return { needsName: !(auth.user?.firstName && auth.user?.lastName), suggestedOrganisationName: null };
  }

  async function loadProfile(signal: AbortSignal): Promise<OnboardingProfile> {
    try {
      const token = await auth.getAccessToken?.();
      if (!token) return profileFallback();
      let result = await fetchOnboardingProfile(token, signal);
      if (result.kind === 'rate_limited') {
        await sleep(RATE_LIMIT_RETRY_MS);
        result = await fetchOnboardingProfile(token, signal);
      }
      if (result.kind === 'ok') return result.profile;
      // An unmount aborts the request, which reads as a network failure; nothing to report.
      if (signal.aborted) return profileFallback();
      logEvent('welcome.profile_unavailable', 'Onboarding profile unavailable; falling back to the session user',
        { status_class: result.kind === 'rate_limited' ? '4xx' : result.statusClass }, 'WARN');
    } catch {
      /* fall through to the fallback profile */
    }
    return profileFallback();
  }

  useEffect(() => {
    if (!nameGateOn) return;
    const controller = new AbortController();
    void (async () => {
      const loaded = await loadProfile(controller.signal);
      if (controller.signal.aborted) return;
      if (!loaded.needsName && auth.user?.id) markNameHandled(auth.user.id);
      setProfile(loaded);
      setHoldForProfile(false);
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the profile is read once the flag reads on; auth identity churns
  }, [nameGateOn]);

  const askName = profile?.needsName === true;

  const initialName = profile?.suggestedOrganisationName ?? organisationNameFromEmail(auth.user?.email) ?? '';

  // System failures (5xx / network / session-switch reject) count toward the cap; a
  // user-fixable fault just shows inline. The 3rd system failure locks into escalation.
  function registerFailure(view: CreateOrgErrorView) {
    if (!view.system) { setSubmitError(view); return; }
    const next = systemFailures + 1;
    setSystemFailures(next);
    if (shouldEscalate(next)) {
      const { trace_id } = getTraceContext();
      // App Insights may be uninitialised or ad-blocked, leaving no trace id — omit the
      // "quote …" clause rather than telling the user to quote nothing.
      const reference = trace_id ? ` and quote ${trace_id}` : '';
      setEscalated(true);
      setSubmitError({
        field: null, system: true,
        message: `Something went wrong setting up your organisation. Please contact support@cloudhouse.com${reference}.`,
      });
      logEvent('welcome.support_escalation_shown', 'Support escalation shown', { attempt_count: next }, 'WARN');
      return;
    }
    setSubmitError(view);
  }

  // false only for a name the API rejects (shown inline). Any other failure must not cost the user
  // their organisation: it is created anyway, the failure counts toward no escalation, and the name
  // is left unhandled so the name gate asks for it later.
  async function saveNameStep(token: string, firstName?: string, lastName?: string): Promise<boolean> {
    if (!askName || !firstName || !lastName) return true;
    if (savedName.current?.firstName === firstName && savedName.current.lastName === lastName) return true;
    const saved = await updateName(token, { firstName, lastName });
    if (saved === 'invalid_name') { setNamesError(INVALID_NAME_MESSAGE); return false; }
    if (saved === 'failed') {
      logEvent('welcome.name_update_failed', 'Saving the user name failed; creating the organisation anyway', {}, 'WARN');
      return true;
    }
    savedName.current = { firstName, lastName };
    if (auth.user?.id) markNameHandled(auth.user.id);
    return true;
  }

  async function createOrganisation(token: string, name: string, slug: string) {
    const { data, error, response } = await api.POST('/organisations', { body: { name, slug }, headers: bearer(token) });

    if (error) {
      const code = (error as { error?: string }).error ?? 'unknown';
      // Token already carries an org — they already have one; just go to the Hub.
      if (code === 'already_in_org') { navigate(ENTERPRISE_URL, { replace: true }); return; }
      const view = mapCreateOrgError(response.status, code);
      logEvent('welcome.organisation_create_failed', 'Create organisation failed',
        { reason: code, status: response.status }, view.system ? 'ERROR' : 'WARN');
      registerFailure(view);
      return;
    }

    // 200 — a fresh create, or the membership pre-flight returning the existing org.
    // Trust the response's organization_id, never the submitted form values.
    const organizationId = data?.organization_id;
    if (!organizationId) { registerFailure(mapCreateOrgError(502, 'workos_failed')); return; }
    // Count the created event only once the org id is confirmed, so a malformed 200
    // doesn't inflate the created/submitted conversion metric.
    trackFunnel(FUNNEL.ORGANISATION_CREATED);
    if (signUpSentFor.current !== organizationId) {
      trackSignUp(); // GA4 conversion; a no-op unless analytics consent started GA4
      signUpSentFor.current = organizationId;
    }
    try {
      await auth.switchToOrganization?.({ organizationId });
      // The common refresh failure redirects the tab (org-scoped) and never reaches here;
      // on success navigate directly rather than waiting for ProtectedRoute's re-render.
      navigate(ENTERPRISE_URL, { replace: true });
    } catch (e) {
      logEvent('welcome.session_switch_failed', 'Session switch failed', {
        'error.type': errorType(e),
        error_message: e instanceof Error ? e.message.slice(0, 300) : 'unknown', // bounded; kept for existing queries
      }, 'ERROR');
      registerFailure({ field: null, system: true, message: 'Your organisation was created but we could not switch you into it. Please try again.' });
    }
  }

  async function handleSubmit({ name, slug, firstName, lastName }: CreateOrganisationValues) {
    if (submitting || escalated) return; // single in-flight; no submits after escalation
    setSubmitting(true);
    setSubmitError(null);
    setNamesError(null);
    trackFunnel(FUNNEL.ORGANISATION_SUBMITTED);
    try {
      const token = await auth.getAccessToken?.();
      if (!token) {
        // Expired session — re-auth path, not a create failure, so it doesn't count to the cap.
        setSubmitError({ field: null, system: false, message: "We couldn't verify your session. Please refresh and sign in again." });
        return;
      }

      if (!(await saveNameStep(token, firstName, lastName))) return;
      await createOrganisation(token, name, slug);
    } catch {
      registerFailure(mapCreateOrgError(0, 'network'));
    } finally {
      setSubmitting(false);
    }
  }

  // A flag that flips off mid-request aborts the profile call, so nothing would ever release the
  // hold: it only applies while the flag still reads on.
  if (holdForProfile && nameGateOn) {
    return (
      <Center h="100vh" style={{ background: tokens.navy }} role="progressbar" aria-label="Loading">
        <Loader size="xl" color="cloudhouseGreen" />
      </Center>
    );
  }

  return (
    <WelcomeView
      onSignOut={() => auth.signOut()}
      createOrganisation={{ initialName, submitting, submitError, locked: escalated, askName, namesError, onSubmit: handleSubmit }}
    />
  );
}
